/**
 * Batched read of community card ratings.
 *
 * Card grids render dozens to hundreds of tiles, so asking the server once per
 * tile turned a normal browse into hundreds of round trips. This reads every
 * requested code in a single query and aggregates in memory.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const schema = z.object({
  codes: z
    .array(z.string().trim().min(3).max(24))
    .min(1)
    .max(300)
    .transform((list) => Array.from(new Set(list.map((c) => c.toUpperCase())))),
  voter: z.string().trim().min(8).max(80).optional(),
});

export interface CardRatingSummary {
  code: string;
  votes: number;
  average: number;
  mine: number;
}

export const getCardRatings = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => schema.parse(input))
  .handler(async ({ data }): Promise<CardRatingSummary[]> => {
    const empty = data.codes.map((code) => ({ code, votes: 0, average: 0, mine: 0 }));
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: rows, error } = await supabaseAdmin
        .from("card_ratings")
        .select("card_code, rating, voter")
        .in("card_code", data.codes);
      if (error || !rows) return empty;

      const totals = new Map<string, { sum: number; votes: number; mine: number }>();
      for (const r of rows) {
        const key = String(r.card_code).toUpperCase();
        const agg = totals.get(key) ?? { sum: 0, votes: 0, mine: 0 };
        agg.sum += Number(r.rating) || 0;
        agg.votes += 1;
        if (data.voter && r.voter === data.voter) agg.mine = Number(r.rating) || 0;
        totals.set(key, agg);
      }

      return data.codes.map((code) => {
        const agg = totals.get(code);
        return {
          code,
          votes: agg?.votes ?? 0,
          average: agg && agg.votes ? Math.round((agg.sum / agg.votes) * 10) / 10 : 0,
          mine: agg?.mine ?? 0,
        };
      });
    } catch {
      return empty;
    }
  });
